<i18n>
{
	"en": {
		"addressesFound": "{count} found",
		"customAddress": "Custom Address",
		"edit": "Edit",
		"failedToFetch": "Failed to search for addresses",
		"mandatoryField": "Address is a mandatory field",
		"missingNameAndPostalCode": "A trip address must contain name and postal code",
		"missingName": "A trip address must have a name",
		"missingPostalCode": "A trip address must have a postal code",
		"invalidNorwegianPostalCode": "Invalid postal code for Norwegian address",
		"cannotBeSameAsFromAddress": "Cannot be the same as from address",
		"cannotBeSameAsToAddress": "Cannot be the same as to address"
    },
    "no": {
		"addressesFound": "{count} treff",
		"customAddress": "Ny adresse",
		"edit": "Rediger",
		"failedToFetch": "Kunne ikke søke etter adresser",
		"mandatoryField": "Adresse er påkrevd",
		"missingNameAndPostalCode": "En turadresse må inneholde navn og postnummer",
		"missingName": "En turadresse må ha et navn",
		"missingPostalCode": "En turadresse må ha et postnummer",
		"invalidNorwegianPostalCode": "Ugyldig postnummer for norsk addresse",
		"cannotBeSameAsFromAddress": "Kan ikke være det samme som fra-addresse",
		"cannotBeSameAsToAddress": "Kan ikke være det samme som til-addresse"
    }
}
</i18n>

<template>
	<t-form-group :title="title">
		<div>
			<t-combobox-button
				ref="input"
				icon="geosearch"
				:limit="$options.SERVER_RETURN_LIMIT"

				:searchValue="searchValue"
				:items="filteredItems"
				:selected="value"

				:isInvalid="isInvalid"
				:validationMessage="generatedValidationMessage"
				showPopoverOnlyOnSearch
				disableButtonTabbing

				@focus="onFocus"
				@input="onInput"
				@blur="onBlur"
				@change="onChange"
				@button:click="setShowDialog(true)"
			>
				<template #optionItemContent="{ item }">
					<div class="item">
						<div class="item-main-text">{{ item.name }}</div>
						<div class="item-sub-text">{{ item.street }}, {{ item.postalCode }} {{ item.postalPlace }}</div>
					</div>
				</template>
			</t-combobox-button>

			<t-after-input :text="addressDisplayText"/>

			<trip-addresses-form-input-dialog
				:isShown="showDialog"
				:initialSearchValue="dialogInitialSearchValue"
				icon="geosearch"
				:onlyTerminals="onlyTerminals"
				@close="setShowDialog(false)"
				@ok="onChange"
			/>
			<edit-trip-address-dialog
				:address="editAddress"
				:isShown="showEditAddressDialog"
				@close="showEditAddressDialog = false"
				@ok="onChange"
			/>
		</div>
	</t-form-group>
</template>

<script>
import { mapActions } from './store/tripAddresses.store.js';
import TripAddressesFormInputDialog from './TripAddressInput/TripAddressDialog';
import EditTripAddressDialog from './TripAddressInput/EditTripAddressDialog';
import TAfterInput from '@/components/evergreen/timpex/inputs/TAfterInput';
import TComboboxButton from '@/components/evergreen/timpex/inputs/TComboboxButton';

export default {
	name: 'TripAddressInput',
	SERVER_RETURN_LIMIT: 50,
	components: { TComboboxButton, TAfterInput, TripAddressesFormInputDialog, EditTripAddressDialog },
	props: {
		value: Object,
		title: String,
		focusNextOnSelected: {
			type: Boolean,
			default: false,
		},
		validationKey: [String, Boolean],
		validationMessage: { type: String },
		isInvalid: {
			type: Boolean,
			default: false,
		},
		onlyTerminals: Boolean,
	},
	data() {
		return {
			items: [],
			showDialog: false,
			showEditAddressDialog: false,
			editAddress: {},
			searchValue: '',
			lastSearch: null,
			searchTimeout: null,
			dirty: false,
			focused: false,
		};
	},
	computed: {
		filteredItems() {
			if (this.searchValue === '')
				return this.items;

			return this.items.filter( item => {

				if (!item)
					return false;

				let loweredSearchValue = this.searchValue.toLowerCase();

				let ret = true;
				for (let c of loweredSearchValue.split(' ')) {
					ret = ret &&
						( (item.postalPlace && item.postalPlace.toLowerCase().includes(c))
							|| ( item.postalCode && item.postalCode.toString().toLowerCase().includes(c) )
							|| ( item.street && item.street.toLowerCase().includes(c))
							|| ( item.name && item.name.toLowerCase().includes(c))
						);
				}
				return ret;
			});
		},
		addressDisplayText() {
			let countryCode = this.value.countryCode ? `${this.value.countryCode}: ` : '';
			let street = this.value.street ? `${this.value.street} - ` : '';
			let postalCode = this.value.postalCode || '';
			let postalPlace = this.value.postalPlace || '';
			return `${countryCode}${street}${postalCode} ${postalPlace}`;
		},
		generatedValidationMessage() {
			return this.validationKey ? this.$t(this.validationKey) : this.validationMessage;
		},
		dialogInitialSearchValue() {
			return this.dirty ? this.searchValue : '';
		},
	},
	watch: {
		value: {
			immediate: true,

			handler(value) {
				this.searchValue = this.getSearchValueFromValue(value);
			}
		}
	},
	methods: {
		...mapActions(['fetchFilteredTripAddresses']),
		onFocus() {
			this.focused = true;
		},
		onInput(searchValue) {
			this.dirty = true;

			this.searchValue = searchValue;

			if (this.shouldCancelNewSearch(searchValue, this.lastSearch, this.items))
				return;

			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {

				this.search(searchValue);

			}, 250);
		},
		shouldCancelNewSearch(searchValue, lastSearch, items) {
			return (
				searchValue === ''
				|| (
					items.length < this.$options.SERVER_RETURN_LIMIT
					&& searchValue.startsWith( lastSearch )
				)
			);
		},
		onChange(value) {

			this.searchValue = this.getSearchValueFromValue(value);

			this.setShowDialog(false);
			this.showEditAddressDialog = false;

			this.$emit('change', value);

			if (
				this.focusNextOnSelected
				&& Object.keys(value).length
			) {
				window.focusNext({ target: this.$refs.input.$el.querySelector('input') });
			}
			this.dirty = false;
		},
		onBlur(event) {
			this.focused = false;

			if (this.isRelatedTargetTheComboboxButton(event))
				return;

			if (
				this.value
				&& ( this.value.id || this.value.name || this.value.postalPlace || this.value.postalCode )
				&& this.searchValue === ''
			)
				this.$emit('change', {});

			else
				this.searchValue = this.getSearchValueFromValue(this.value);
		},
		isRelatedTargetTheComboboxButton(event) {

			return event.relatedTarget === this.$refs.input.$el.querySelector('button');

		},
		async search(searchValue) {
			try {
				let filter = { filterString: searchValue };
				if (this.onlyTerminals) filter.isTerminal = true;
				this.items = await this.fetchFilteredTripAddresses(filter);

				if (
					!this.focused
					&& this.items
					&& this.items.length === 1
				) {
					this.$emit('change', this.items[0]);
					this.searchValue = this.getSearchValueFromValue(this.items[0]);
				}
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
			this.lastSearch = searchValue;
		},
		getSearchValueFromValue(value) {

			if (value && value.name && value.name.length)
				return value.name;
			else
				return '';
		},
		setShowDialog(showDialog) {

			if (this.showDialog === true && showDialog === false)
				this.searchValue = this.getSearchValueFromValue(this.value);

			this.showDialog = showDialog;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

</style>
